import React, { useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { notifyChange } from "../notifications/notify";
import { updateCreativeDetails } from "../../services/creativesService";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";
import { CreativeModalHeader, CreativePrimaryButton } from "./CreativeModalParts";
import { CreativeTargetingFields, CREATIVE_INPUT_STYLE, CREATIVE_LABEL_STYLE } from "./CreativeTargetingFields";
import { audioTypesLabel, dateRangeError, hasUnreadableAudioTypes, normalizeAudioTypes, resolveAudioTypes } from "./creativeTargeting";

function formatDetailsMarkdown({ name, audioTypes, startDate, endDate }) {
  return [
    `**Name:** ${name || "(unnamed)"}`,
    `**Audio Types:** ${audioTypesLabel(audioTypes)}`,
    `**Start date:** ${startDate || "(none)"}`,
    `**End date:** ${endDate || "(none)"}`,
  ].join("\n");
}

export function EditCreativeModal({ creative, advertiserId, onSaved, onClose }) {
  const { isSupportUser } = useAuth();
  const [original] = useState(() => ({
    name: (creative.name ?? "").trim(),
    startDate: creative.start_date ?? "",
    endDate: creative.end_date ?? "",
    audioTypes: resolveAudioTypes(normalizeAudioTypes(creative.audio_types)),
  }));
  const [name, setName] = useState(original.name);
  const [startDate, setStartDate] = useState(original.startDate);
  const [endDate, setEndDate] = useState(original.endDate);
  const [audioTypes, setAudioTypes] = useState(original.audioTypes);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Escape closes, except mid-save: the edit may already have reached some assets.
  const motion = useModalMotion(onClose, { dismissible: !saving });
  const { requestClose } = motion;

  const edited = { name: name.trim(), startDate, endDate, audioTypes: resolveAudioTypes(audioTypes) };
  const hasChanges = JSON.stringify(edited) !== JSON.stringify(original);
  const hasUnreadableTypes = hasUnreadableAudioTypes(creative.stored_audio_types ?? creative.audio_types);
  const isSaveDisabled = saving || !hasChanges || hasUnreadableTypes || !!dateRangeError(startDate, endDate);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    const { error } = await updateCreativeDetails({
      advertiserId,
      creativeId: creative.id,
      name: edited.name,
      startDate: startDate || null,
      endDate: endDate || null,
      audioTypes: edited.audioTypes,
    });
    setSaving(false);
    if (error) { setSaveError(error.message); return; }
    if (!isSupportUser) {
      notifyChange({
        supabase,
        eventType: "Creative Gallery - Creative Details",
        advertiserId,
        previousState: formatDetailsMarkdown(original),
        newState: formatDetailsMarkdown(edited),
      });
    }
    onSaved?.();
    requestClose();
  };

  return (
    <ModalShell motion={motion} labelledBy="edit-creative-title" maxWidth={560}>
      <CreativeModalHeader eyebrow="Creative" title="Edit details" titleId="edit-creative-title" onClose={requestClose} closeDisabled={saving} />

      <div style={{ padding:"24px 28px" }}>
        <div style={{ marginBottom:16 }}>
          <label htmlFor="edit-creative-name" style={CREATIVE_LABEL_STYLE}>Name</label>
          <input id="edit-creative-name" type="text" value={name} onChange={e => setName(e.target.value)} style={CREATIVE_INPUT_STYLE} />
        </div>

        <CreativeTargetingFields
          startDate={startDate} endDate={endDate} audioTypes={audioTypes}
          onStartDateChange={setStartDate} onEndDateChange={setEndDate} onAudioTypesChange={setAudioTypes}
        />

        {hasUnreadableTypes && <div role="alert" style={{ marginBottom:12, fontSize:12, color:"#e53935" }}>This creative has inventory types that can't be edited here.{!isSupportUser && " Contact support to change it."}</div>}
        {saveError && <div key={saveError} role="alert" className="ncm-reveal" style={{ marginBottom:12, fontSize:12, color:"#e53935" }}>{saveError}</div>}
        <CreativePrimaryButton onClick={handleSave} disabled={isSaveDisabled} busy={saving}>
          {saving ? "Saving…" : "Save changes"}
        </CreativePrimaryButton>
      </div>
    </ModalShell>
  );
}
